/* ==========================================================================
   PiPellet — theme behaviour
   No dependencies. Everything degrades gracefully without JS.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------- Quantity input --- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.min = parseFloat(this.dataset.min || '1');
      this.step = parseFloat(this.dataset.step || '1');
      this.max = this.dataset.max ? parseFloat(this.dataset.max) : Infinity;

      this.querySelector('[data-qty-down]')?.addEventListener('click', () => this.nudge(-1));
      this.querySelector('[data-qty-up]')?.addEventListener('click', () => this.nudge(1));
      this.input.addEventListener('change', () => this.normalise());
    }

    nudge(direction) {
      const current = parseFloat(this.input.value) || this.min;
      this.input.value = current + direction * this.step;
      this.normalise();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Snap to the nearest valid step above the minimum — this is what makes
       Shopify B2B quantity rules (min / increment / max) hold in the UI. */
    normalise() {
      let value = parseFloat(this.input.value);
      if (isNaN(value)) value = this.min;
      if (value < this.min) value = this.min;
      if (value > this.max) value = this.max;

      const steps = Math.round((value - this.min) / this.step);
      value = this.min + steps * this.step;
      if (value > this.max) value -= this.step;

      this.input.value = Number(value.toFixed(4));
    }
  }
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }

  /* --------------------------------------------------- Dialog focus trap --- */
  /* Both the mobile nav and the cart drawer are `role="dialog" aria-modal`.
     aria-modal tells assistive tech to seal the dialog, but it does nothing for
     a sighted keyboard user's Tab order — focus still walks into the page
     behind. These helpers keep Tab inside the open dialog, mark the trigger with
     aria-expanded, and hand focus back to that trigger on close. */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let activeDialog = null;
  let dialogTrigger = null;
  let lastFocused = null;

  function openDialog(dialog, trigger) {
    if (!dialog) return;
    activeDialog = dialog;
    dialogTrigger = trigger || null;
    lastFocused = document.activeElement;
    dialog.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    trigger?.setAttribute('aria-expanded', 'true');
    (dialog.querySelector('[autofocus]') || dialog.querySelector(FOCUSABLE))?.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.hasAttribute('open')) return;
    dialog.removeAttribute('open');
    document.body.style.overflow = '';
    dialogTrigger?.setAttribute('aria-expanded', 'false');
    if (activeDialog === dialog) { activeDialog = null; dialogTrigger = null; }
    /* Return focus to whatever opened the dialog, so a keyboard user lands back
       where they were rather than at the top of the document. */
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function trapFocus(container, event) {
    const nodes = container.querySelectorAll(FOCUSABLE);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------ Mobile nav --- */
  const mobileNav = document.getElementById('MobileNav');
  const openNav = (trigger) => openDialog(mobileNav, trigger);
  const closeNav = () => closeDialog(mobileNav);

  document.addEventListener('click', (event) => {
    const navOpen = event.target.closest('[data-mobile-nav-open]');
    if (navOpen) { event.preventDefault(); openNav(navOpen); }
    if (event.target.closest('[data-mobile-nav-close]')) { event.preventDefault(); closeNav(); }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { closeNav(); closeCart(); }
    if (event.key === 'Tab' && activeDialog) trapFocus(activeDialog, event);
  });

  /* ---------------------------------------------------------- Cart drawer --- */
  const drawer = document.getElementById('CartDrawer');
  const openCart = (trigger) => {
    if (!drawer) return;
    openDialog(drawer, trigger);
    refreshCart();
  };
  const closeCart = () => closeDialog(drawer);

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-toggle]');
    if (toggle && drawer) { event.preventDefault(); openCart(toggle); }
    if (event.target.closest('[data-cart-close]')) { event.preventDefault(); closeCart(); }
  });

  async function refreshCart() {
    if (!drawer) return;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`);
      const text = await response.text();
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const fresh = parsed.querySelector('[data-cart-drawer-content]');
      const target = drawer.querySelector('[data-cart-drawer-content]');
      if (fresh && target) target.innerHTML = fresh.innerHTML;
    } catch (error) {
      console.warn('Cart refresh failed', error);
    }
  }

  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = count;
      node.hidden = count === 0;
    });
  }

  /* ---------------------------------------------- AJAX add to cart (quick) --- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-quick-add]');
    if (!form || !drawer) return;

    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const original = button?.textContent;
    if (button) { button.disabled = true; button.textContent = '…'; }

    try {
      const body = new FormData(form);
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body
      });
      const data = await response.json();

      if (data.status) {
        alert(data.description || data.message);
      } else {
        const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
        setCartCount(cart.item_count);
        openCart();
      }
    } catch (error) {
      form.submit();
    } finally {
      if (button) { button.disabled = false; button.textContent = original; }
    }
  });

  /* ----------------------------------------------- Cart line item updates --- */
  const cartUpdateTimers = new Map();
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-line-qty]');
    if (!input) return;

    /* The stepper fires a change per click; debounce so holding + doesn't
       queue a dozen requests. Keyed per line — a single shared timer would let
       one line's edit cancel another's. */
    const line = input.dataset.line;
    clearTimeout(cartUpdateTimers.get(line));
    cartUpdateTimers.set(line, setTimeout(async () => {
      cartUpdateTimers.delete(line);
      input.disabled = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(input.dataset.line), quantity: Number(input.value) })
        });
        const cart = await response.json();
        setCartCount(cart.item_count);
        /* The same stepper drives both the drawer and the /cart page. Inside the
           drawer, re-render the section instead of reloading — a full reload
           throws away the drawer we just opened. On the cart page there is no
           drawer to preserve, so fall back to a reload to refresh totals. */
        if (drawer && drawer.contains(input)) {
          await refreshCart();
        } else {
          window.location.reload();
        }
      } catch (error) {
        input.disabled = false;
        console.warn('Cart update failed', error);
      }
    }, 400));
  });

  /* --------------------------------------------------------- Product page --- */
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const main = gallery.querySelector('[data-gallery-main] img');
    gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.full;
        const srcset = thumb.dataset.srcset;
        if (main && src) {
          /* The main image is rendered with a srcset, which outranks src. Swap it
             for the thumbnail's own candidates, or clear it so src can win. */
          if (srcset) {
            main.srcset = srcset;
          } else {
            main.removeAttribute('srcset');
            main.removeAttribute('sizes');
          }
          main.src = src;
        }
        gallery.querySelectorAll('[data-gallery-thumb]').forEach((t) => t.setAttribute('aria-current', 'false'));
        thumb.setAttribute('aria-current', 'true');
      });
    });
  });

  /* Variant switching via the Section Rendering API */
  document.querySelectorAll('[data-variant-picker]').forEach((picker) => {
    picker.addEventListener('change', () => {
      const form = picker.closest('form') || document;
      const selected = Array.from(picker.querySelectorAll('input:checked')).map((i) => i.value);
      const variants = JSON.parse(picker.dataset.variants || '[]');
      const match = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      if (!match) return;

      const idField = form.querySelector('input[name="id"]');
      if (idField) idField.value = match.id;

      const url = new URL(window.location);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);

      document.querySelectorAll('[data-variant-refresh]').forEach(async (node) => {
        const sectionId = node.dataset.variantRefresh;
        const res = await fetch(`${window.location.pathname}?variant=${match.id}&section_id=${sectionId}`);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = html.querySelector(`[data-variant-refresh="${sectionId}"]`);
        if (fresh) node.innerHTML = fresh.innerHTML;
      });
    });
  });

  /* ------------------------------------------------------------- Parallax --- */
  /* Three numbers drive the banner, and the CSS reads nothing else. --p is
     how far the banner has travelled through the viewport; --pp and --pw are
     the pile's and the wordmark's own slices of that. They overlap for most
     of the window on purpose: the point is watching the two close on each
     other, not one arriving and then the other.

     The span is adaptive — from "top edge at the viewport bottom" to "banner
     centred" — so a tall banner does not finish its sequence while it is still
     half off screen. The written value chases its target rather than tracking
     scroll exactly, which is what gives the layers weight. */
  const PARALLAX_WINDOW = 1.45;  // of the distance to centre; >1 slows it down
  const PILE_SETTLES_AT = 0.92;  // pile is home this far through
  const MARK_STARTS_AT = 0.18;   // wordmark waits until here
  const MARK_ENDS_AT = 1;
  const INTRO_MS = 1100;         // played when the banner is already in view
  const CHASE = 0.14;

  const clamp01 = (value) => (value < 0 ? 0 : value > 1 ? 1 : value);
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  document.querySelectorAll('[data-parallax]').forEach((root) => {
    const stage = root.querySelector('[data-parallax-stage]');
    if (!stage) return;

    const write = (p) => {
      root.style.setProperty('--p', p.toFixed(4));
      root.style.setProperty('--pp', clamp01(p / PILE_SETTLES_AT).toFixed(4));
      root.style.setProperty(
        '--pw',
        clamp01((p - MARK_STARTS_AT) / (MARK_ENDS_AT - MARK_STARTS_AT)).toFixed(4)
      );
    };

    /* Nothing to soften here — the banner is the same height either way, so
       reduced motion simply gets the finished composition. */
    if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      write(1);
      return;
    }

    let current = 0;
    let running = false;
    let visible = false;
    let introStart = null;

    function target() {
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const box = stage.getBoundingClientRect();
      const span = ((viewport + box.height) / 2) * PARALLAX_WINDOW;
      if (span <= 0) return 1;
      return clamp01((viewport - box.top) / span);
    }

    function frame() {
      /* A banner already on screen at load has no scroll to be driven by, so
         it plays itself in once and then hands over. */
      if (introStart !== null) {
        const elapsed = (window.performance.now() - introStart) / INTRO_MS;
        if (elapsed < 1) {
          current = easeOut(clamp01(elapsed));
          write(current);
          window.requestAnimationFrame(frame);
          return;
        }
        introStart = null;
      }

      const goal = easeOut(target());
      current += (goal - current) * CHASE;
      if (Math.abs(goal - current) < 0.0008) current = goal;
      write(current);

      if (visible) {
        window.requestAnimationFrame(frame);
      } else {
        /* Park it exactly where it belongs, so scrolling back finds the banner
           already correct rather than catching up. */
        running = false;
        current = goal;
        write(goal);
      }
    }

    function start() {
      if (running) return;
      running = true;
      window.requestAnimationFrame(frame);
    }

    new IntersectionObserver(
      (entries) => {
        visible = entries[0].isIntersecting;
        if (visible) start();
        else write(easeOut(target()));
      },
      { rootMargin: '25% 0px' }
    ).observe(stage);

    const viewport = window.innerHeight || document.documentElement.clientHeight;
    if (stage.getBoundingClientRect().top < viewport * 0.6) {
      introStart = window.performance.now();
      visible = true;
      current = 0;
      write(0);
    } else {
      current = easeOut(target());
      write(current);
    }
    start();
  });
})();
